<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="header-left">
          <h3 class="modal-title">AI Analysis Details</h3>
          <div class="analysis-badges">
            <span :class="['type-badge', analysis?.analysis_type]">
              {{ analysis?.analysis_type === 'call' ? 'Phone Call' : 'WhatsApp' }}
            </span>
            <span :class="['satisfaction-badge', getSatisfactionClass(analysis?.customer_satisfaction_prediction)]">
              {{ formatSatisfaction(analysis?.customer_satisfaction_prediction) }}
            </span>
          </div>
        </div>
        <button @click="closeModal" class="modal-close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <div class="modal-body" v-if="analysis">
        <!-- Key Metrics Overview -->
        <div class="metrics-overview">
          <div class="metric-card">
            <div class="metric-icon satisfaction">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM8.5 11c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm7 0c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5 14.67 11 15.5 11zm-3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ analysis.customer_satisfaction_score }}%</div>
              <div class="metric-label">Satisfaction Score</div>
            </div>
          </div>

          <div class="metric-card" v-if="analysis.agent_performance_score">
            <div class="metric-icon performance">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2z"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ analysis.agent_performance_score }}%</div>
              <div class="metric-label">Performance Score</div>
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-icon escalation">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ analysis.escalation_risk_score }}%</div>
              <div class="metric-label">Escalation Risk</div>
            </div>
          </div>

          <div class="metric-card" v-if="analysis.empathy_score">
            <div class="metric-icon empathy">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5 2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z"/>
              </svg>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ analysis.empathy_score }}%</div>
              <div class="metric-label">Empathy Score</div>
            </div>
          </div>
        </div>

        <!-- Basic Information -->
        <div class="info-section">
          <h4 class="section-title">Analysis Information</h4>
          <div class="info-grid">
            <div class="info-item">
              <label class="info-label">Analysis ID</label>
              <div class="info-value">#{{ analysis.id }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Customer Phone</label>
              <div class="info-value customer-phone">{{ analysis.customer_phone }}</div>
            </div>
            <div class="info-item" v-if="analysis.agent_name">
              <label class="info-label">Agent</label>
              <div class="info-value">{{ analysis.agent_name }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Conversation Length</label>
              <div class="info-value">{{ analysis.conversation_length }} messages</div>
            </div>
            <div class="info-item">
              <label class="info-label">Date & Time</label>
              <div class="info-value">{{ formatDateTime(analysis.created_at) }}</div>
            </div>
            <div class="info-item">
              <label class="info-label">Processing Time</label>
              <div class="info-value">{{ analysis.processing_time_seconds }}s</div>
            </div>
          </div>
        </div>

        <!-- Sentiment Analysis -->
        <div class="info-section">
          <h4 class="section-title">Sentiment Analysis</h4>
          <div class="sentiment-breakdown">
            <div class="sentiment-chart">
              <div class="sentiment-bar">
                <div class="sentiment-segment positive" :style="{ width: analysis.overall_sentiment_positive + '%' }"></div>
                <div class="sentiment-segment neutral" :style="{ width: analysis.overall_sentiment_neutral + '%' }"></div>
                <div class="sentiment-segment negative" :style="{ width: analysis.overall_sentiment_negative + '%' }"></div>
              </div>
              <div class="sentiment-labels">
                <div class="sentiment-label positive">
                  <div class="label-dot"></div>
                  <span>Positive {{ analysis.overall_sentiment_positive }}%</span>
                </div>
                <div class="sentiment-label neutral">
                  <div class="label-dot"></div>
                  <span>Neutral {{ analysis.overall_sentiment_neutral }}%</span>
                </div>
                <div class="sentiment-label negative">
                  <div class="label-dot"></div>
                  <span>Negative {{ analysis.overall_sentiment_negative }}%</span>
                </div>
              </div>
            </div>
            <div class="sentiment-progression">
              <div class="progression-item">
                <label>Start Sentiment</label>
                <span :class="['sentiment-badge', analysis.sentiment_start]">
                  {{ formatSentiment(analysis.sentiment_start) }}
                </span>
              </div>
              <div class="progression-arrow">→</div>
              <div class="progression-item">
                <label>End Sentiment</label>
                <span :class="['sentiment-badge', analysis.sentiment_end]">
                  {{ formatSentiment(analysis.sentiment_end) }}
                </span>
              </div>
              <div class="progression-result">
                <label>Overall Progression</label>
                <span :class="['progression-badge', getProgressionClass(analysis.emotional_progression)]">
                  {{ formatProgression(analysis.emotional_progression) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Performance Metrics -->
        <div class="info-section" v-if="analysis.agent_name">
          <h4 class="section-title">Agent Performance</h4>
          <div class="performance-grid">
            <div class="performance-item">
              <label>Talk Time Ratio</label>
              <div class="talk-time-chart">
                <div class="talk-bar">
                  <div class="agent-time" :style="{ width: analysis.talk_time_ratio_agent + '%' }"></div>
                  <div class="customer-time" :style="{ width: analysis.talk_time_ratio_customer + '%' }"></div>
                </div>
                <div class="talk-labels">
                  <span class="agent-label">Agent {{ analysis.talk_time_ratio_agent }}%</span>
                  <span class="customer-label">Customer {{ analysis.talk_time_ratio_customer }}%</span>
                </div>
              </div>
            </div>
            <div class="performance-item">
              <label>Response Time</label>
              <div class="metric-display">{{ analysis.average_response_time }}s avg</div>
            </div>
            <div class="performance-item">
              <label>Compliance Score</label>
              <div class="score-display">{{ analysis.compliance_score }}%</div>
            </div>
            <div class="performance-item">
              <label>Product Knowledge</label>
              <div class="score-display">{{ analysis.product_knowledge_score }}%</div>
            </div>
          </div>
        </div>

        <!-- Key Topics -->
        <div class="info-section" v-if="analysis.key_topics && analysis.key_topics.length">
          <h4 class="section-title">Key Topics Discussed</h4>
          <div class="topics-cloud">
            <span
              v-for="(topic, index) in analysis.key_topics"
              :key="index"
              class="topic-chip"
            >
              {{ topic }}
            </span>
          </div>
        </div>

        <!-- Detected Issues -->
        <div class="info-section" v-if="analysis.detected_issues && analysis.detected_issues.length">
          <h4 class="section-title">Detected Issues</h4>
          <div class="issues-list">
            <div
              v-for="(issue, index) in analysis.detected_issues"
              :key="index"
              class="issue-item"
            >
              <div class="issue-icon">⚠️</div>
              <span>{{ issue }}</span>
            </div>
          </div>
        </div>

        <!-- Positive Highlights -->
        <div class="info-section" v-if="analysis.positive_highlights && analysis.positive_highlights.length">
          <h4 class="section-title">Positive Highlights</h4>
          <div class="highlights-list">
            <div
              v-for="(highlight, index) in analysis.positive_highlights"
              :key="index"
              class="highlight-item"
            >
              <div class="highlight-icon">✅</div>
              <span>{{ highlight }}</span>
            </div>
          </div>
        </div>

        <!-- Escalation Triggers -->
        <div class="info-section" v-if="analysis.escalation_triggers && analysis.escalation_triggers.length">
          <h4 class="section-title">Escalation Triggers</h4>
          <div class="triggers-list">
            <div
              v-for="(trigger, index) in analysis.escalation_triggers"
              :key="index"
              class="trigger-item"
            >
              <div class="trigger-icon">🚨</div>
              <span class="trigger-text">{{ trigger }}</span>
            </div>
          </div>
        </div>

        <!-- Improvement Suggestions -->
        <div class="info-section" v-if="analysis.improvement_suggestions && analysis.improvement_suggestions.length">
          <h4 class="section-title">Improvement Suggestions</h4>
          <div class="suggestions-list">
            <div
              v-for="(suggestion, index) in analysis.improvement_suggestions"
              :key="index"
              class="suggestion-item"
            >
              <div class="suggestion-icon">💡</div>
              <span>{{ suggestion }}</span>
            </div>
          </div>
        </div>

        <!-- AI Summary -->
        <div class="info-section" v-if="analysis.ai_summary">
          <h4 class="section-title">AI Summary</h4>
          <div class="ai-summary">
            <div class="summary-icon">🤖</div>
            <p>{{ analysis.ai_summary }}</p>
          </div>
        </div>

        <!-- Resolution Status -->
        <div class="info-section">
          <h4 class="section-title">Resolution Status</h4>
          <div class="resolution-status">
            <div class="status-card">
              <div class="status-icon">
                <svg v-if="analysis.issue_resolution_status === 'resolved'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                <svg v-else-if="analysis.issue_resolution_status === 'escalated'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
                <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                </svg>
              </div>
              <div class="status-content">
                <div class="status-text">{{ formatResolution(analysis.issue_resolution_status) }}</div>
                <div class="status-details">
                  <div class="detail-item">
                    <span class="detail-label">First Contact Resolution:</span>
                    <span class="detail-value">{{ analysis.first_contact_resolution ? 'Yes' : 'No' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Abandonment Risk:</span>
                    <span class="detail-value">{{ analysis.abandonment_risk_score }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <button @click="closeModal" class="action-btn primary">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIAnalysisDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    analysis: {
      type: Object,
      default: null
    }
  },
  methods: {
    closeModal () {
      this.$emit('close')
    },

    formatDateTime (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleString()
    },

    formatSatisfaction (prediction) {
      const map = {
        satisfied: 'Satisfied',
        neutral: 'Neutral',
        dissatisfied: 'Dissatisfied'
      }
      return map[prediction] || prediction
    },

    formatSentiment (sentiment) {
      const map = {
        positive: 'Positive',
        negative: 'Negative',
        neutral: 'Neutral'
      }
      return map[sentiment] || sentiment
    },

    formatProgression (progression) {
      const map = {
        improved: 'Improved',
        declined: 'Declined',
        stable: 'Stable'
      }
      return map[progression] || progression
    },

    formatResolution (status) {
      const map = {
        resolved: 'Resolved',
        escalated: 'Escalated',
        pending: 'Pending'
      }
      return map[status] || status
    },

    getSatisfactionClass (prediction) {
      const map = {
        satisfied: 'satisfied',
        neutral: 'neutral',
        dissatisfied: 'dissatisfied'
      }
      return map[prediction] || 'neutral'
    },

    getProgressionClass (progression) {
      const map = {
        improved: 'positive',
        declined: 'negative',
        stable: 'neutral'
      }
      return map[progression] || 'neutral'
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(8px);
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 20px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 1000px;
  width: 95%;
  max-height: 95vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px;
  border-bottom: 1px solid var(--border-primary);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.analysis-badges {
  display: flex;
  gap: 12px;
}

.type-badge {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.type-badge.call {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.type-badge.whatsapp_conversation {
  background: linear-gradient(135deg, #10b981, #059669);
}

.satisfaction-badge {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.satisfaction-badge.satisfied {
  background: linear-gradient(135deg, #10b981, #059669);
}

.satisfaction-badge.neutral {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.satisfaction-badge.dissatisfied {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 12px;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
  transform: scale(1.1);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
}

/* Metrics Overview */
.metrics-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px var(--shadow-primary);
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.metric-icon.satisfaction {
  background: linear-gradient(135deg, #10b981, #059669);
}

.metric-icon.performance {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.metric-icon.escalation {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.metric-icon.empathy {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
}

.metric-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
  font-weight: 500;
}

/* Info Sections */
.info-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 2px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 15px;
  color: var(--text-primary);
  font-weight: 500;
}

.customer-phone {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  background: var(--bg-secondary);
  padding: 8px 12px;
  border-radius: 8px;
  display: inline-block;
}

/* Sentiment Analysis */
.sentiment-breakdown {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sentiment-chart {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sentiment-bar {
  height: 12px;
  background: var(--bg-tertiary);
  border-radius: 6px;
  display: flex;
  overflow: hidden;
}

.sentiment-segment.positive {
  background: linear-gradient(90deg, #10b981, #059669);
}

.sentiment-segment.neutral {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.sentiment-segment.negative {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.sentiment-labels {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.sentiment-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.label-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.sentiment-label.positive .label-dot {
  background: #10b981;
}

.sentiment-label.neutral .label-dot {
  background: #f59e0b;
}

.sentiment-label.negative .label-dot {
  background: #ef4444;
}

.sentiment-progression {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 12px;
  flex-wrap: wrap;
}

.progression-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.progression-item label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.sentiment-badge,
.progression-badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.sentiment-badge.positive,
.progression-badge.positive {
  background: #10b981;
}

.sentiment-badge.neutral,
.progression-badge.neutral {
  background: #f59e0b;
}

.sentiment-badge.negative,
.progression-badge.negative {
  background: #ef4444;
}

.progression-arrow {
  font-size: 20px;
  color: var(--text-secondary);
  font-weight: bold;
}

/* Performance Metrics */
.performance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.performance-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--bg-secondary);
  border-radius: 12px;
}

.performance-item label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
}

.talk-time-chart {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.talk-bar {
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  display: flex;
  overflow: hidden;
}

.agent-time {
  background: #667eea;
}

.customer-time {
  background: #10b981;
}

.talk-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.agent-label {
  color: #667eea;
  font-weight: 500;
}

.customer-label {
  color: #10b981;
  font-weight: 500;
}

.metric-display,
.score-display {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

/* Topics, Issues, Highlights */
.topics-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.topic-chip {
  padding: 8px 16px;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.issues-list,
.highlights-list,
.triggers-list,
.suggestions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.issue-item,
.highlight-item,
.trigger-item,
.suggestion-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
}

.issue-icon,
.highlight-icon,
.trigger-icon,
.suggestion-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.trigger-text {
  font-style: italic;
  color: var(--text-primary);
}

/* AI Summary */
.ai-summary {
  display: flex;
  gap: 16px;
  padding: 20px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

.summary-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.ai-summary p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-primary);
}

/* Resolution Status */
.resolution-status {
  display: flex;
  justify-content: center;
}

.status-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  min-width: 300px;
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.status-content {
  flex: 1;
}

.status-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.status-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.detail-label {
  color: var(--text-secondary);
}

.detail-value {
  color: var(--text-primary);
  font-weight: 500;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: center;
  padding: 24px 28px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn.primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.action-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-content {
    width: 98%;
    max-height: 98vh;
  }

  .modal-header,
  .modal-body,
  .modal-actions {
    padding: 20px;
  }

  .metrics-overview {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .sentiment-progression {
    flex-direction: column;
    gap: 16px;
  }

  .performance-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .status-card {
    flex-direction: column;
    text-align: center;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .metrics-overview {
    grid-template-columns: 1fr;
  }

  .header-left {
    align-items: flex-start;
  }

  .analysis-badges {
    flex-wrap: wrap;
  }
}
</style>
